import { createContext } from "react";
import { API, type IAPI, type OperationParams } from "@/services/APIService";
import { AuthService, type IAuthService } from "@/services/Auth";
import { useVenue } from "@/hooks/useVenue";
import { OrderService, type IOrderService } from "@/services/Orders";
import {
  type IOrganisationService,
  OrganisationService,
} from "@/services/Organisation";
import {
  type IOperationsService,
  OperationsService,
} from "@/services/Operations/Operations";
import {
  DevicesService,
  type IDevicesService,
} from "@/services/Devices/Devices";
import { TagsService, type ITagsService } from "@/services/Tags/Tags";
import {
  type ITableLocationService,
  TableLocationService,
} from "@/services/TableLocations";
import {
  OldRedeemablesService,
  type IOldRedeemablesService,
} from "@/services/OldRedeemables/Redeemables";
import {
  type ITransactionService,
  TransactionService,
} from "@/services/Transactions";
import {
  PrinterService,
  type IPrinterService,
} from "@/services/Printers/Printers";
import {
  ReportsService,
  type IReportsService,
} from "@/services/Reports/ReportsService";
import { type ITeamService, TeamService } from "@/services/Team/Team";
import {
  RedeemablesService,
  type IRedeemablesService,
} from "@/services/Redeemables/Redeemables";
import {
  type INotificationsService,
  NotificationsService,
} from "@/services/Notifications/NotificationsService";

const APIContext = createContext<
  | {
      api: Partial<IAPI>;
      authService: IAuthService;
      ordersService: IOrderService;
      transactionsService: ITransactionService;
      organisationService: IOrganisationService;
      operationsService: IOperationsService;
      devicesService: IDevicesService;
      tagsService: ITagsService;
      tableLocationService: ITableLocationService;
      oldRedeemablesService: IOldRedeemablesService;
      redeemablesService: IRedeemablesService;
      printersService: IPrinterService;
      reportsService: IReportsService;
      teamService: ITeamService;
      notificationsService: INotificationsService;
    }
  | undefined
>(undefined);

const APIProvider = ({ children }) => {
  const { selectedVenue } = useVenue();

  const includeVenueId = (params: OperationParams) => {
    if (params.venueSpecific && selectedVenue) {
      params.headers = {
        ...params.headers,
        "X-Venue-Id": selectedVenue,
        "X-Use-Venue-Token": true,
      };
    }
    return params;
  };

  const api = new API();

  const get = async <T = unknown,>(
    url: string,
    params: OperationParams,
  ): Promise<T> => {
    return api.get<T>(
      url,
      params.venueSpecific ? includeVenueId(params) : params,
    );
  };

  const post = async <T = unknown,>(
    url: string,
    params: OperationParams,
  ): Promise<T> => {
    return api.post<T>(
      url,
      params.venueSpecific ? includeVenueId(params) : params,
    );
  };

  const patch = async <T = unknown,>(
    url: string,
    params: OperationParams,
  ): Promise<T> => {
    return api.patch<T>(
      url,
      params.venueSpecific ? includeVenueId(params) : params,
    );
  };

  const deleteAPI = async <T = unknown,>(
    url: string,
    params: OperationParams,
  ): Promise<T> => {
    return api.delete<T>(
      url,
      params.venueSpecific ? includeVenueId(params) : params,
    );
  };
  const getAPI = async (url: string, params: OperationParams) => {
    return api.getAPI(url, params);
  };
  const postAPI = async (url: string, params: OperationParams) => {
    return api.postAPI(url, params);
  };
  const getFile = async (url: string, params: OperationParams) => {
    return api.getFile(url, params);
  };

  const apiObj = {
    get,
    post,
    patch,
    delete: deleteAPI,
    getAPI,
    postAPI,
    getFile,
    selectedVenue: selectedVenue || null,
  };

  return (
    <APIContext.Provider
      value={{
        api: apiObj,
        authService: new AuthService(apiObj),
        ordersService: new OrderService(apiObj),
        transactionsService: new TransactionService(apiObj),
        organisationService: new OrganisationService(apiObj),
        operationsService: new OperationsService(apiObj),
        devicesService: new DevicesService(apiObj),
        tagsService: new TagsService(apiObj),
        tableLocationService: new TableLocationService(apiObj),
        oldRedeemablesService: new OldRedeemablesService(apiObj),
        redeemablesService: new RedeemablesService(apiObj),
        printersService: new PrinterService(apiObj),
        reportsService: new ReportsService(apiObj),
        teamService: new TeamService(apiObj),
        notificationsService: new NotificationsService(apiObj),
      }}
    >
      {children}
    </APIContext.Provider>
  );
};
export { APIProvider, APIContext as default };
